/**
 * Module to handle event dispatching and listening.
 * @module Events
 */
class Events {
  /**
   * Constructor method for the class.
   */
  constructor() {
    // Create the placeholder for the listeners
    this.listeners = {};
  }

  /**
   * Method to register listeners with events.
   * @param {String} name  The name of the event.
   * @param {Function} callback  The function to call when the event is fired.
   */
  subscribe(name, callback) {
    if (typeof name !== "string" || typeof callback !== "function") {
      throw new TypeError("Invalid arguments provided to subscribe method");
    }

    // If no listeners exist by the name, create the list and add the handler
    if (!this.listeners[name]) {
      this.listeners[name] = [];
    }

    // Add it to the list of listeners, if not already present
    if (this.listeners[name].indexOf(callback) < 0) {
      this.listeners[name].push(callback);
    }
  }

  /**
   * Method to unregister listeners from events.
   * @param {String} name  The name of the event.
   * @param {Function} [callback]  The function to remove from the listeners.
   */
  unsubscribe(name, callback = null) {
    if (typeof name !== "string") {
      throw new TypeError("Invalid event name provided to unsubscribe method");
    }

    const listeners = this.listeners[name];

    // If no listeners exist by the name, skip the rest
    if (!listeners) {
      return;
    }

    // If no callback is specified, delete all handlers for the event type
    if (!callback) {
      delete this.listeners[name];
      return;
    }

    if (typeof callback !== "function") {
      throw new TypeError("Invalid callback provided to unsubscribe method");
    }

    // Otherwise, iterate over all handlers and remove the specified callback
    this.listeners[name] = listeners.filter(
      (listener) => listener !== callback
    );

    // If no more listeners for the event, delete the key
    if (this.listeners[name].length === 0) {
      delete this.listeners[name];
    }
  }

  /**
   * Method to trigger events.
   * @param {String} name  The name of the event.
   * @param {Object} [data]  The data to pass when the event is fired.
   */
  emit(name, data) {
    if (typeof name !== "string") {
      throw new TypeError("Invalid event name provided to emit method");
    }

    // Get the list of listeners or fallback to an empty array
    // Iterate over the listeners to call the handler with the data
    (this.listeners[name] || []).forEach((listener) => listener(data));
  }
}

// Export the class as a singleton / static class by creating an instance
export default new Events();
